/**
 * Supabase client — lazily initialised.
 *
 * Env vars (frontend/.env):
 *   REACT_APP_SUPABASE_URL
 *   REACT_APP_SUPABASE_ANON_KEY
 *
 * If either is missing, `supabase` exports as null and all consumers MUST
 * fall back to their localStorage stores so the app keeps working offline.
 */
import { createClient } from "@supabase/supabase-js";

const url = process.env.REACT_APP_SUPABASE_URL;
const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
    // Non-fatal — app continues with localStorage stores. We surface a single,
    // helpful warning so deployments without Supabase env vars don't crash.
    // eslint-disable-next-line no-console
    console.warn(
        "[Neyra] Supabase env vars missing — running in offline mode. " +
        "Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to enable database sync."
    );
}

export const supabase =
    url && anonKey
        ? createClient(url, anonKey, {
              auth: {
                  persistSession: true,
                  autoRefreshToken: true,
                  detectSessionInUrl: true,
              },
          })
        : null;

export const isSupabaseEnabled = () => !!supabase;

/** Shape mirrored from DB when enabled; otherwise undefined and caller falls back. */
export const SUPABASE_TABLES = {
    USERS: "users",
    PROFILES: "profiles",
    BUSINESS_PROFILES: "business_profiles",
    SERVICES: "services",
    EVENTS: "events",
    BOOKINGS: "bookings",
    MESSAGES: "messages",
    REVIEWS: "reviews",
    WALLETS: "wallets",
    TRANSACTIONS: "transactions",
    REFERRALS: "referrals",
    NOTIFICATIONS: "notifications",
    REPORTS: "reports",
    WITHDRAWALS: "withdrawals",
    SETTINGS: "platform_settings",
    FOLLOWERS: "followers",
    BOOSTS: "boosts",
    VERIFICATIONS: "verifications",
    PANIC_ALERTS: "panic_alerts",
};
